import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import AppDialog, { AppDialogConfirmButton } from "common/Components/AppDialog";
import DataTable, { DataTableEmptyState } from "common/Components/DataTable";
import StatusPill from "common/Components/StatusPill";
import { CustomizedSwitch } from "styles/formStyleComponents";
import {
  CUSTOMERS_PAGE_CONSTANTS,
  DEFAULT_CUSTOMERS_LIST_FILTERS,
  buildCustomersResultSummary,
  hasActiveCustomersListFilters,
} from "./Customers.constants";
import {
  ClearFiltersButton,
  CustomerCodeText,
  CustomerNameLink,
  CustomerStatusCell,
  CustomerStatusLabel,
  CustomersTableFrame,
  InactiveCustomerTitleStatus,
  ResultsBar,
  ResultsSummary,
} from "./Customers.styles";

const INACTIVE_DIALOG_TITLE_ID = "inactive-customer-dialog-title";

const CustomersList = ({
  groups,
  loading = false,
  pagination,
  listFilters = DEFAULT_CUSTOMERS_LIST_FILTERS,
  onPageChange,
  onRowsPerPageChange,
  onListFiltersChange,
  onToggleActive,
  onRowOpen,
}) => {
  const [inactiveCustomer, setInactiveCustomer] = useState(null);
  const [inactiveDialogOpen, setInactiveDialogOpen] = useState(false);
  const filtersActive = hasActiveCustomersListFilters(listFilters);
  const shownCount = pagination.totalCount;
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    if (!filtersActive) {
      setGrandTotal(Number(shownCount) || 0);
    }
  }, [filtersActive, shownCount]);

  const resultSummary = buildCustomersResultSummary(
    shownCount,
    filtersActive,
    grandTotal
  );

  const handleClearFilters = useCallback(() => {
    onListFiltersChange({ ...DEFAULT_CUSTOMERS_LIST_FILTERS });
  }, [onListFiltersChange]);

  const handleOpenCustomer = useCallback(
    (row) => {
      if (row?.isDeleted) {
        setInactiveCustomer(row);
        setInactiveDialogOpen(true);
        return;
      }
      onRowOpen?.(row.source);
    },
    [onRowOpen]
  );

  const rows = useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).map((group) => ({
        id: group.code ?? group.customerCode ?? group.name,
        name: group.name ?? "",
        code: group.code ?? group.customerCode ?? "",
        isDeleted: Boolean(group.isDeleted),
        source: group,
      })),
    [groups]
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        label: CUSTOMERS_PAGE_CONSTANTS.COLUMNS.NAME,
        sortable: false,
        minWidth: 180,
        filter: { type: "text" },
        render: (value, row) => (
          <CustomerNameLink
            title={value}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenCustomer(row);
            }}
          >
            {value || "—"}
          </CustomerNameLink>
        ),
      },
      {
        id: "code",
        label: CUSTOMERS_PAGE_CONSTANTS.COLUMNS.CODE,
        sortable: false,
        minWidth: 140,
        filter: { type: "text" },
        render: (value) => (
          <CustomerCodeText title={value}>{value || "—"}</CustomerCodeText>
        ),
      },
      {
        id: "isDeleted",
        label: CUSTOMERS_PAGE_CONSTANTS.COLUMNS.STATUS,
        sortable: false,
        minWidth: 140,
        align: "left",
        filter: {
          type: "select",
          options: CUSTOMERS_PAGE_CONSTANTS.STATUS_FILTER_OPTIONS,
        },
        render: (_, row) => {
          const isActive = !row.isDeleted;

          return (
            <CustomerStatusCell onClick={(event) => event.stopPropagation()}>
              <CustomizedSwitch
                checked={isActive}
                onChange={(event) =>
                  onToggleActive?.(event.target.checked, row.source)
                }
                inputProps={{
                  "aria-label": `${row.name || "Customer"} active`,
                }}
              />
              <CustomerStatusLabel $active={isActive}>
                {isActive
                  ? CUSTOMERS_PAGE_CONSTANTS.STATUS_ACTIVE
                  : CUSTOMERS_PAGE_CONSTANTS.STATUS_INACTIVE}
              </CustomerStatusLabel>
            </CustomerStatusCell>
          );
        },
      },
    ],
    [handleOpenCustomer, onToggleActive]
  );

  const emptyState = (
    <DataTableEmptyState
      title={CUSTOMERS_PAGE_CONSTANTS.EMPTY_STATE_TITLE}
      hint={CUSTOMERS_PAGE_CONSTANTS.EMPTY_STATE_HINT}
    />
  );

  return (
    <CustomersTableFrame>
      <ResultsBar>
        <ResultsSummary aria-live="polite" component="p">
          {resultSummary}
        </ResultsSummary>
        <ClearFiltersButton
          type="button"
          $active={filtersActive}
          disabled={!filtersActive}
          onClick={handleClearFilters}
          data-testid="clear-filters-btn"
        >
          {CUSTOMERS_PAGE_CONSTANTS.CLEAR_FILTERS_LABEL}
        </ClearFiltersButton>
      </ResultsBar>
      <DataTable
        title={CUSTOMERS_PAGE_CONSTANTS.TABLE_TITLE}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        showExport={false}
        serverMode
        page={pagination.page}
        totalRows={pagination.totalCount}
        defaultRowsPerPage={pagination.size}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        onRowClick={(row) => handleOpenCustomer(row)}
        onFilterChange={onListFiltersChange}
        filterValues={listFilters}
        applyTextFiltersOnCommit
        renderEmptyState={() => emptyState}
        testId="customers-table"
      />
      <AppDialog
        open={inactiveDialogOpen}
        onClose={() => setInactiveDialogOpen(false)}
        aria-labelledby={INACTIVE_DIALOG_TITLE_ID}
        heading={
          <>
            {inactiveCustomer?.name
              ? `Customer: ${inactiveCustomer.name}`
              : CUSTOMERS_PAGE_CONSTANTS.CUSTOMER_TITLE_FALLBACK}
            <InactiveCustomerTitleStatus>
              <StatusPill
                label={CUSTOMERS_PAGE_CONSTANTS.STATUS_INACTIVE}
                tone="neutral"
              />
            </InactiveCustomerTitleStatus>
          </>
        }
        footer={
          <AppDialogConfirmButton
            type="button"
            variant="contained"
            onClick={() => setInactiveDialogOpen(false)}
            autoFocus
          >
            {CUSTOMERS_PAGE_CONSTANTS.INACTIVE_DIALOG_OK_LABEL}
          </AppDialogConfirmButton>
        }
      >
        {CUSTOMERS_PAGE_CONSTANTS.INACTIVE_DIALOG_MESSAGE}
      </AppDialog>
    </CustomersTableFrame>
  );
};

CustomersList.propTypes = {
  groups: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
  }).isRequired,
  listFilters: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
    isDeleted: PropTypes.string,
  }),
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onListFiltersChange: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
  onRowOpen: PropTypes.func.isRequired,
};

CustomersList.defaultProps = {
  groups: [],
  loading: false,
  listFilters: DEFAULT_CUSTOMERS_LIST_FILTERS,
};

export default CustomersList;
